import { type Address, Dtos, formatAddress } from "@inrev/common";
import {
	CompanyNameFeinFields,
	ContactAddressField,
} from "@portal/components/features/contact-forms";
import { FormField, FormFieldGroup } from "@portal/components/form";
import { FormCheckboxOption } from "@portal/components/form/fields";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { createFormResolver } from "@portal/components/multiform/utils/formResolver";
import { Button } from "@portal/components/primitives";
import { FormSchemaProvider } from "@portal/providers/FormSchemaProvider";
import { useEffect, useState } from "react";
import { type FieldError, useForm, useFormState } from "react-hook-form";
import { useAddressValidation } from "../../../../../api";
import { getSchemaDefaults } from "../../../../../utils/form";
import type { CompanyFormData } from "../schema";

const wasPreviouslySubmitted = (data: CompanyFormData | undefined): boolean => {
	if (!data) return false;
	return !!data.name;
};

type AccountCompanyModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (company: CompanyFormData) => void;
	initialData?: CompanyFormData;
	hidePrimaryCompanyOption?: boolean;
	existingNames?: string[];
};

const companyFormSchema = Dtos.Company.Create.FromSubmission.Request.schema;
const EMPTY_EXISTING_NAMES: string[] = [];

export const AccountCompanyModal = ({
	isOpen,
	onClose,
	onSubmit,
	initialData,
	hidePrimaryCompanyOption,
	existingNames,
}: AccountCompanyModalProps) => {
	if (!isOpen) return null;

	return (
		<AccountCompanyModalContent
			onClose={onClose}
			onSubmit={onSubmit}
			initialData={initialData}
			hidePrimaryCompanyOption={hidePrimaryCompanyOption}
			existingNames={existingNames}
		/>
	);
};

const AccountCompanyModalContent = ({
	onClose,
	onSubmit,
	initialData,
	hidePrimaryCompanyOption,
	existingNames = EMPTY_EXISTING_NAMES,
}: Omit<AccountCompanyModalProps, "isOpen">) => {
	const formMethods = useForm<CompanyFormData>({
		mode: "onBlur",
		resolver: createFormResolver(companyFormSchema),
	});

	/**
	 * use reset() not defaultValues so field clears don't snap back to initialData (RHF behavior)
	 */
	useEffect(() => {
		formMethods.reset(
			{ ...getSchemaDefaults(companyFormSchema), ...initialData },
			{ keepDefaultValues: true },
		);
	}, [initialData]);

	const isCurrentlyPrimary = initialData?.isPrimaryCompany === true;
	const [wasPreviouslySubmittedState] = useState(wasPreviouslySubmitted(initialData));
	const { errors } = useFormState({ control: formMethods.control });

	const [addressString, setAddressString] = useState(
		initialData?.address ? formatAddress(initialData.address) : "",
	);

	const { validatedAddress, addressFieldControl } = useAddressValidation({
		value: addressString,
		error: errors.address as FieldError | undefined,
		setValue: (value: string) => setAddressString(value),
		setError: (error: FieldError) => formMethods.setError("address", error),
		clearError: () => formMethods.clearErrors("address"),
	});

	useEffect(() => {
		if (validatedAddress) {
			formMethods.setValue("address", validatedAddress as unknown as Address);
		}
	}, [validatedAddress]);

	const handleSave = () => {
		formMethods.handleSubmit(() => {
			const values = formMethods.getValues();
			const trimmedName = values.name?.trim();
			if (
				trimmedName &&
				existingNames.some((n) => n.trim().toLowerCase() === trimmedName.toLowerCase())
			) {
				formMethods.setError("name", {
					type: "custom",
					message: "A company with this name has already been added",
				});
				return;
			}
			onSubmit({ ...values, name: trimmedName });
		})();
	};

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader header="Company" onClose={onClose}>
				<div className="max-w-[820px] min-w-[747px] px-[40px] pt-[25px] pb-[35px]">
					<FormSchemaProvider value={companyFormSchema}>
						<form className="w-full h-fit flex flex-col space-y-[25px]">
							<FormFieldGroup condensed>
								<CompanyNameFeinFields
									formMethods={formMethods}
									partialRequiresSubmit={!wasPreviouslySubmittedState}
								/>
								<ContactAddressField
									formMethods={formMethods}
									addressControl={addressFieldControl}
									partialRequiresSubmit={!wasPreviouslySubmittedState}
									compact
								/>
								{!hidePrimaryCompanyOption && (
									<FormField>
										<FormCheckboxOption
											name="isPrimaryCompany"
											control={formMethods.control}
											label="Set as primary company for account"
											compact
											disabled={isCurrentlyPrimary}
										/>
									</FormField>
								)}
							</FormFieldGroup>
							<div className="w-full flex justify-center mt-[5px]">
								<Button
									variant="solid"
									onClick={handleSave}
									color="primary"
									size="md"
									className="w-[150px]"
									loading={formMethods.formState.isSubmitting}
								>
									Save
								</Button>
							</div>
						</form>
					</FormSchemaProvider>
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
